/**
 * Base API client class for Picasso
 * Eliminates duplication between imageApi.ts and videoApi.ts
 */

import { auth } from "@/lib/picasso-firebase"

const API_BASE_URL = import.meta.env.VITE_BASE_URL

export type QueryParams = Record<string, string | number | boolean | undefined>

/**
 * Base API client class with common functionality
 */
export class BaseApiClient {
  /**
   * Get authenticated headers with Bearer token
   * @returns Promise<Record<string, string>> Headers object with Authorization
   */
  static async getAuthHeaders(): Promise<Record<string, string>> {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error("Authentication required")
    }

    const token = await currentUser.getIdToken()
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    }
  }

  /**
   * Handle API errors consistently across the application
   * @param error - The error object
   * @param operation - Description of the operation that failed
   * @returns string - Formatted error message
   */
  static handleApiError(error: unknown, operation: string): string {
    if (error instanceof Error) {
      return `Failed to ${operation}: ${error.message}`
    }
    return `Failed to ${operation}: Unknown error`
  }

  /**
   * Check if current user is authenticated
   * @returns boolean
   */
  static isAuthenticated(): boolean {
    return !!auth.currentUser
  }

  /**
   * Get current user's email
   * @returns string | null
   */
  static getCurrentUserEmail(): string | null {
    return auth.currentUser?.email || null
  }

  /**
   * Build URL with query parameters
   * @param baseUrl - Base URL
   * @param params - Query parameters
   * @returns URL with parameters
   */
  static buildUrlWithParams(baseUrl: string, params: QueryParams): string {
    const url = new URL(baseUrl)
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.append(key, value.toString())
      }
    }
    return url.toString()
  }

  /**
   * Generic API request handler with consistent error handling
   * @param url - The API endpoint URL
   * @param options - Fetch options
   * @param errorContext - Context for error logging
   * @returns Promise<T> - Parsed JSON response
   */
  static async makeRequest<T>(
    url: string,
    options: RequestInit,
    errorContext: string
  ): Promise<T> {
    try {
      const response = await fetch(url, options)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.detail || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      return await response.json()
    } catch (error) {
      console.error(`❌ ${errorContext}:`, error)
      throw error
    }
  }

  /**
   * Make authenticated API request
   * @param url - API endpoint URL
   * @param options - Fetch options
   * @returns Promise<Response>
   */
  static async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const headers = await BaseApiClient.getAuthHeaders()
    
    return fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    })
  }

  /**
   * Generic GET request helper
   * @param endpoint - API endpoint
   * @param errorContext - Context for error logging
   * @param params - Query parameters
   * @returns Promise<T>
   */
  static async getRequest<T>(
    endpoint: string,
    errorContext: string,
    params: QueryParams = {}
  ): Promise<T> {
    const headers = await BaseApiClient.getAuthHeaders()
    const url = BaseApiClient.buildUrlWithParams(`${API_BASE_URL}${endpoint}`, params)
    
    return BaseApiClient.makeRequest<T>(url, { method: "GET", headers }, errorContext)
  }

  /**
   * Generic POST request helper
   * @param endpoint - API endpoint
   * @param data - Request body data
   * @param errorContext - Context for error logging
   * @returns Promise<T>
   */
  static async postRequest<T>(
    endpoint: string,
    data: any,
    errorContext: string
  ): Promise<T> {
    const headers = await BaseApiClient.getAuthHeaders()
    
    return BaseApiClient.makeRequest<T>(
      `${API_BASE_URL}${endpoint}`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      },
      errorContext
    )
  }

  /**
   * Generic PUT request helper
   * @param endpoint - API endpoint
   * @param data - Request body data
   * @param errorContext - Context for error logging
   * @returns Promise<T>
   */
  static async putRequest<T>(
    endpoint: string,
    data: any,
    errorContext: string
  ): Promise<T> {
    const headers = await BaseApiClient.getAuthHeaders()
    
    return BaseApiClient.makeRequest<T>(
      `${API_BASE_URL}${endpoint}`,
      {
        method: "PUT",
        headers,
        body: JSON.stringify(data),
      },
      errorContext
    )
  }

  /**
   * Generic DELETE request helper
   * @param endpoint - API endpoint
   * @param errorContext - Context for error logging
   * @returns Promise<T>
   */
  static async deleteRequest<T>(endpoint: string, errorContext: string): Promise<T> {
    const headers = await BaseApiClient.getAuthHeaders()
    
    return BaseApiClient.makeRequest<T>(
      `${API_BASE_URL}${endpoint}`,
      { method: "DELETE", headers },
      errorContext
    )
  }

  /**
   * Generic PATCH request helper
   * @param endpoint - API endpoint
   * @param data - Request body data
   * @param errorContext - Context for error logging
   * @returns Promise<T>
   */
  static async patchRequest<T>(
    endpoint: string,
    data: any,
    errorContext: string
  ): Promise<T> {
    const headers = await BaseApiClient.getAuthHeaders()
    
    return BaseApiClient.makeRequest<T>(
      `${API_BASE_URL}${endpoint}`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      },
      errorContext
    )
  }
}